export const NERIS_BOAT_ASSET_MANIFEST_URL =
  "/assets/boats/manifest.json";

/**
 * Source-derived central Neris centreline, simplified offline from the
 * checked-in GRPK `Neris` features. It is gameplay navigation data, not a
 * generic vehicle road graph.
 */
export const NERIS_NAVIGATION_ROUTE = Object.freeze([
  { x: -2100.75, z: 134.75 },
  { x: -1980.25, z: 299.5 },
  { x: -1824.25, z: 404.5 },
  { x: -1578, z: 473.25 },
  { x: -1464, z: 451.5 },
  { x: -1381.5, z: 406.75 },
  { x: -1315.5, z: 341.25 },
  { x: -1282.25, z: 258.25 },
  { x: -1229, z: -315.25 },
  { x: -1174.5, z: -528 },
  { x: -1082.75, z: -719.25 },
  { x: -993.5, z: -782.5 },
  { x: -905, z: -801.25 },
  { x: -750.75, z: -796 },
  { x: -640.75, z: -757 },
  { x: -367.75, z: -481.5 },
  { x: -239.25, z: -400.25 },
  { x: -124.5, z: -369.75 },
  { x: 318.75, z: -316.5 },
  { x: 413.25, z: -270.25 },
  { x: 575.25, z: -120.75 },
  { x: 658.5, z: -76 },
  { x: 754, z: -70.75 },
  { x: 934.75, z: -110.75 },
  { x: 1018.25, z: -177 },
  { x: 1194.5, z: -390.25 },
  { x: 1453.75, z: -610.75 },
  { x: 1709.25, z: -1044.5 },
  { x: 1772, z: -1218.25 },
]);

export interface NerisBoatDock {
  readonly id: string;
  readonly label: string;
  readonly boatId: string;
  readonly berth: Readonly<{ x: number; z: number }>;
  readonly exit: Readonly<{ x: number; y: number; z: number }>;
  readonly yawRadians: number;
}

export const NERIS_BOAT_DOCKS: readonly NerisBoatDock[] = Object.freeze([
  {
    id: "white-bridge-stop",
    label: "White Bridge stop",
    boatId: "neris-electric-1",
    berth: { x: -346, z: -454 },
    exit: { x: -335, y: 12, z: -442 },
    yawRadians: 0.782,
  },
  {
    id: "green-bridge-stop",
    label: "Green Bridge stop",
    boatId: "neris-electric-2",
    berth: { x: 115, z: -300 },
    exit: { x: 110, y: 12.214, z: -286 },
    yawRadians: 1.451,
  },
  {
    id: "king-mindaugas-stop",
    label: "King Mindaugas stop",
    boatId: "neris-electric-3",
    berth: { x: 630, z: -42 },
    exit: { x: 615, y: 12.459, z: -25 },
    yawRadians: 1.077,
  },
  {
    id: "zverynas-stop",
    label: "Žvėrynas stop",
    boatId: "neris-electric-4",
    berth: { x: -1215, z: -278 },
    exit: { x: -1200, y: 17.938, z: -260 },
    yawRadians: 3.049,
  },
  {
    id: "business-triangle-stop",
    label: "Business Triangle stop",
    boatId: "neris-electric-5",
    berth: { x: -1270, z: 290 },
    exit: { x: -1260, y: 12.066, z: 310 },
    yawRadians: 3.049,
  },
]);

/**
 * PROVISIONAL — USER TUNING REQUIRED.
 *
 * These constants deliberately expose boat feel without claiming final
 * movement tuning. The mechanic remains constrained to the Neris corridor.
 */
export const BOAT_TUNING = Object.freeze({
  maximumForwardSpeedMps: 8.2,
  maximumReverseSpeedMps: 2.8,
  accelerationMps2: 2.4,
  brakingMps2: 3.6,
  dragMps2: 1.15,
  turnSpeedRadiansPerSecond: 0.72,
  navigationHalfWidthM: 39,
  bankResponseSpeedScale: 0.38,
  boardingRadiusM: 18,
  dockingRadiusM: 24,
  maximumDismountSpeedMps: 1.1,
  playerDeckOffsetM: 0.82,
  cameraTargetHeightOffsetM: 2.0,
});

/** Render-only wake treatment; boat handling remains in BOAT_TUNING. */
export const BOAT_PRESENTATION_CONFIG = Object.freeze({
  wakeWidthM: 3.1,
  wakeLengthM: 10.5,
  wakeOffsetBehindBoatM: 6.5,
  wakeSurfaceLiftM: 0.035,
  wakeMinimumSpeedMps: 0.45,
  wakeFullStrengthSpeedMps: 5.5,
  wakeColour: 0xb7cbc3,
  wakeMaximumOpacity: 0.34,
});

export function nerisSurfaceElevationM(x: number): number {
  return Math.max(5, Math.min(11.6, 9.95 + x * 0.0008));
}
